import React from 'react';
import { useUpdateContact } from 'src/hooks/api/contacts/contacts';
import { useAlert } from 'src/contexts/alert-context';
import { type FullName } from 'src/types/users';
import { type ApiQueryParams } from 'src/api/generated';
import { InlineFullNameInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-full-name-input/index';

interface ContactFullNameInputProps extends FullName {
  contactId: number;
  contactsQueryParams?: ApiQueryParams;
}

/**
 * An inline-editable contact full name input component that allows users to edit
 * a contact's first and last name directly in the UI with automatic save/cancel functionality.
 *
 * @param contactId - The ID of the contact to edit
 * @param firstName - The contact's first name
 * @param lastName - The contact's last name
 * @param contactsQueryParams - Optional query parameters for cache invalidation
 */
const ContactFullNameInput: React.FC<ContactFullNameInputProps> = ({
  contactId,
  firstName,
  lastName,
  contactsQueryParams,
}) => {
  const { showAlert } = useAlert();

  const handleContactUpdateSuccess = () => {
    // TODO: check when implementing i18n
    showAlert('El nombre ha sido guardado correctamente.', 'success');
  };

  const handleContactUpdateError = () => {
    // TODO: check when implementing i18n
    showAlert(
      'Ocurrió un error actualizando el nombre. Por favor revisa tu conexión e intenta nuevamente.',
      'error'
    );
  };

  const cacheKey = contactsQueryParams ? [contactsQueryParams] : [];
  const { updateContact, loading } = useUpdateContact(
    handleContactUpdateSuccess,
    handleContactUpdateError,
    cacheKey
  );

  const handleContactFullNameUpdate = (
    newValues: FullName
  ): Promise<boolean> => {
    updateContact({
      contactId,
      updatedContact: {
        firstName: newValues.firstName,
        lastName: newValues.lastName,
      },
    });
    return Promise.resolve(true);
  };

  return (
    <InlineFullNameInput
      onSave={async (newValues: FullName): Promise<boolean> =>
        await handleContactFullNameUpdate(newValues)
      }
      loading={loading}
      firstName={firstName}
      lastName={lastName}
    />
  );
};

export default ContactFullNameInput;
